import * as Sentry from '@sentry/react';
/**
 * The hand-off for a viewport capture travelling from the viewer to the report.
 *
 * ONE DURABLE STORE, SEVERAL NUDGES. Every capture lands here and is read out
 * exactly once; BroadcastChannel, the `storage` event and the same-window
 * CustomEvent below only say "look again". That split is deliberate:
 *
 *  - It fixes the multi-window case. sessionStorage is per-tab and is only
 *    COPIED into a window at `window.open` time, so a capture made after the
 *    report window opened was invisible to it, and the BroadcastChannel message
 *    that was supposed to cover that is live-only with no replay — miss it while
 *    the window is still mounting and the image is gone for good. localStorage
 *    is shared by every window of the origin, which is also the pattern
 *    `lib/lesionCaptureBridge.ts` already uses for exactly this hand-off.
 *
 *  - It removes a duplicate. The viewer used to write the buffer AND post the
 *    image itself, and the report page added from both paths with no dedup, so
 *    whenever both arrived the same capture was appended twice. Carrying the
 *    payload in one place makes that unrepresentable rather than merely rare.
 *
 *  - Losing a notification is now survivable. The store is durable and
 *    consume-on-read, so a missed nudge costs a delay until the next drain
 *    trigger (focus, visibility, mount), not the image.
 *
 * PHI lives here, so entries are read-once, time-boxed by {@link CAPTURE_TTL_MS},
 * and cleared on sign-out via {@link clearAllCaptureBuffers}.
 */
const CAPTURE_BUFFER_PREFIX = 'newlumen_capture_buffer_';
/** Fired on `window` after a capture is buffered; `detail.studyId` names the study. */
export const REPORT_CAPTURE_BUFFERED_EVENT = 'newlumen:report-capture-buffered';
/** Cross-window nudge. Carries only a study id — never image bytes (see above). */
export const REPORT_CAPTURE_CHANNEL = 'newlumen-report-capture';
/**
 * How long a buffered capture stays useful. Long enough to survive a radiologist
 * stepping away mid-read, short enough that images don't accumulate on a shared
 * workstation for the next user.
 */
export const CAPTURE_TTL_MS = 60 * 60 * 1000;
/**
 * Caps, because a capture is a full-resolution PNG data URL (~1-2MB each) and
 * localStorage gives an origin roughly 5MB. Without a ceiling the third capture
 * of a session could throw QuotaExceededError, and the old code answered that by
 * swallowing the error — so the image vanished silently. Trimming the oldest
 * keeps the mechanism working and reports what it dropped.
 */
export const MAX_BUFFERED_IMAGES = 12;
export const MAX_BUFFERED_BYTES = 3_500_000;
/** The storage key a study's buffer lives at. Exported so listeners filtering
 *  `storage` events match on the same string this module writes. */
export function captureBufferKey(studyId) {
    return CAPTURE_BUFFER_PREFIX + studyId;
}
function keyFor(studyId) {
    return captureBufferKey(studyId);
}
function approxBytes(image) {
    return image.dataUrl?.length ?? 0;
}
function readRaw(studyId) {
    try {
        const raw = localStorage.getItem(keyFor(studyId));
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return [];
        return parsed.filter((entry) => !!entry && typeof entry === 'object' && !!entry.id);
    }
    catch {
        // Unparseable or storage unavailable (private mode). Treat as empty rather
        // than throwing into a render path.
        return [];
    }
}
/** Drop entries past their TTL. Applied on both read and write so a buffer no
 *  report ever opened still ages out the next time the viewer touches it. */
function withoutExpired(images, now) {
    return images.filter((img) => {
        const at = typeof img.bufferedAt === 'number' ? img.bufferedAt : now;
        return now - at < CAPTURE_TTL_MS;
    });
}
/** Trim oldest-first until the list fits both caps. Returns what was dropped. */
function withinCaps(images) {
    let kept = images;
    let dropped = 0;
    while (kept.length > MAX_BUFFERED_IMAGES) {
        kept = kept.slice(1);
        dropped++;
    }
    // Always leave at least the newest capture, even if it alone exceeds the byte
    // cap: dropping the thing the user just asked for is worse than being over.
    let total = kept.reduce((sum, img) => sum + approxBytes(img), 0);
    while (kept.length > 1 && total > MAX_BUFFERED_BYTES) {
        total -= approxBytes(kept[0]);
        kept = kept.slice(1);
        dropped++;
    }
    return { kept, dropped };
}
function write(studyId, images) {
    const key = keyFor(studyId);
    try {
        if (images.length === 0) {
            localStorage.removeItem(key);
            return;
        }
        localStorage.setItem(key, JSON.stringify(images));
    }
    catch (err) {
        // Quota, or storage disabled. Retry once with only the newest capture before
        // giving up, so a big backlog can't wedge the newest hand-off.
        try {
            localStorage.setItem(key, JSON.stringify(images.slice(-1)));
            Sentry.captureMessage('[capture-buffer] trimmed to newest capture after a storage failure', {
                level: 'warning',
            });
        }
        catch {
            Sentry.captureException(err, { tags: { area: 'report-capture-buffer', step: 'write' } });
        }
    }
}
/** Tell this window, and every other window of the origin, that a capture landed. */
function notify(studyId) {
    try {
        window.dispatchEvent(new CustomEvent(REPORT_CAPTURE_BUFFERED_EVENT, { detail: { studyId } }));
    }
    catch {
        // Non-DOM environment; nothing local to notify.
    }
    // Other windows also get the native `storage` event for free, but that does not
    // fire in the window that wrote, and it does not fire at all when the write was
    // rejected. The channel nudge covers both, and carries no payload so a missed
    // one costs only latency.
    try {
        if (typeof BroadcastChannel !== 'undefined') {
            const channel = new BroadcastChannel(REPORT_CAPTURE_CHANNEL);
            channel.postMessage({ studyId });
            channel.close();
        }
    }
    catch {
        // Channel unavailable; the drain still runs on mount, focus and visibility.
    }
}
/**
 * Delete every expired buffer across all studies, not just the one being touched.
 *
 * The TTL is otherwise only a READ filter, which is not a retention guarantee: a
 * study whose report was never opened keeps its capture bytes on disk forever,
 * because nothing reads that key again to filter it. These are patient images in
 * localStorage, so they need an actual delete. Cheap — the scan is over key names,
 * and only expired entries are parsed.
 */
export function sweepExpiredCaptureBuffers(now = Date.now()) {
    try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(CAPTURE_BUFFER_PREFIX))
                keys.push(key);
        }
        for (const key of keys) {
            const studyId = key.slice(CAPTURE_BUFFER_PREFIX.length);
            const stored = readRaw(studyId);
            const live = withoutExpired(stored, now);
            if (live.length !== stored.length)
                write(studyId, live);
        }
    }
    catch {
        // Storage unavailable; nothing to sweep.
    }
}
/** Buffer a captured image for a study. Survives navigation and reaches other windows. */
export function bufferCapturedImage(studyId, image, now = Date.now()) {
    // Every write is an opportunity to age out other studies' abandoned buffers.
    sweepExpiredCaptureBuffers(now);
    const existing = withoutExpired(readRaw(studyId), now);
    // Guard against the same capture being buffered twice (a retried hand-off, or
    // two viewports racing on one id) — ids are the identity downstream too.
    const deduped = existing.filter((img) => img.id !== image.id);
    const { kept, dropped } = withinCaps([...deduped, { ...image, bufferedAt: now }]);
    if (dropped > 0) {
        const msg = `[capture-buffer] dropped ${dropped} older capture(s) for study ${studyId} to stay within the buffer caps`;
        console.warn(msg);
        Sentry.captureMessage(msg, { level: 'warning' });
    }
    write(studyId, kept);
    notify(studyId);
}
/** Consume (read + delete) all buffered captures for a study. */
export function consumeBufferedImages(studyId, now = Date.now()) {
    const stored = readRaw(studyId);
    // The overwhelmingly common call is a drain on an empty buffer — focus and
    // visibilitychange fire constantly — so cost nothing when there is nothing.
    if (stored.length === 0)
        return [];
    const images = withoutExpired(stored, now);
    // Remove before returning: a second drain (another window, a focus event) must
    // not hand the same capture out twice.
    write(studyId, []);
    return images.map(({ bufferedAt, ...image }) => {
        void bufferedAt;
        return image;
    });
}
/**
 * Drop every buffered capture. Called on sign-out so one reader's key images are
 * not sitting in storage for whoever signs in next on a shared workstation.
 */
export function clearAllCaptureBuffers() {
    try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(CAPTURE_BUFFER_PREFIX))
                keys.push(key);
        }
        for (const key of keys)
            localStorage.removeItem(key);
    }
    catch {
        // Storage unavailable; nothing to clear.
    }
}
